/**
 * <ItemPicker> — searchable dropdown over every in-game item AND egg, so
 * admins never have to remember an exact ItemID spelling. Backed by the
 * same `/admin/give/catalog` data the Give page uses; the merged catalog is
 * cached at module level so every picker on the page shares one fetch.
 *
 * `value` is the raw ItemID (what PalDefender expects). Free text is still
 * accepted — typing an id that isn't in the catalog keeps working.
 * `only="egg"` limits the list to pal eggs; by default eggs are EXCLUDED,
 * because an egg handed out as a plain item arrives with no pal inside and
 * never hatches (they belong in a dedicated `PalEggs` prize block).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Check, X } from "lucide-react";
import { api } from "@/lib/api";

let _catalog = null;     // [{id, name, image, category, kind}]
let _inflight = null;

async function fetchCatalog() {
  if (_catalog) return _catalog;
  if (_inflight) return _inflight;
  _inflight = Promise.all([
    api.get("/admin/give/catalog?kind=item").catch(() => ({ data: {} })),
    api.get("/admin/give/catalog?kind=egg").catch(() => ({ data: {} })),
  ])
    .then(([itemRes, eggRes]) => {
      const rows = [];
      const seen = new Set();
      const push = (list, kind) => {
        (list || []).forEach((r) => {
          if (!r?.id || seen.has(r.id)) return;
          seen.add(r.id);
          rows.push({ id: r.id, name: r.name || r.id, image: r.image, category: r.category, kind });
        });
      };
      push(itemRes.data?.results || [], "item");
      push(eggRes.data?.results || [], "egg");
      _catalog = rows;
      return rows;
    })
    .finally(() => { _inflight = null; });
  return _inflight;
}

export function useItemCatalog() {
  const [catalog, setCatalog] = useState(_catalog || []);
  useEffect(() => {
    let cancelled = false;
    fetchCatalog().then((rows) => { if (!cancelled) setCatalog(rows || []); });
    return () => { cancelled = true; };
  }, []);
  return catalog;
}

/* ---- Egg picker -------------------------------------------------------
 * Eggs are NOT plain items: PalDefender needs a rolled PalID inside each
 * egg or it never hatches. The options here come from our 67 hatch pools
 * (which includes the modded mutation + World Tree eggs the item browser
 * doesn't list), and each row shows what it can hatch.
 */
let _eggs = null;
let _eggsInflight = null;

export function useEggOptions() {
  const [eggs, setEggs] = useState(_eggs || []);
  useEffect(() => {
    let cancelled = false;
    if (!_eggs && !_eggsInflight) {
      _eggsInflight = api.get("/admin/raids/egg-options")
        .then(({ data }) => { _eggs = data?.eggs || []; return _eggs; })
        .catch(() => [])
        .finally(() => { _eggsInflight = null; });
    }
    (_eggsInflight || Promise.resolve(_eggs || []))
      .then((rows) => { if (!cancelled) setEggs(rows || []); });
    return () => { cancelled = true; };
  }, []);
  return eggs;
}

export function EggPicker({ value, onChange, testId }) {
  const eggs = useEggOptions();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const selected = eggs.find((e) => e.id === value) || null;
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return eggs;
    return eggs.filter((e) => e.name.toLowerCase().includes(q) || e.id.toLowerCase().includes(q));
  }, [eggs, query]);

  return (
    <div className="relative" ref={boxRef} data-testid={testId}>
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); setQuery(""); }}
        className="input input-sm w-full flex items-center gap-1.5 text-left"
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        {selected?.image && (
          <img src={selected.image} alt="" className="w-4 h-4 object-contain shrink-0" loading="lazy" />
        )}
        <span className={`truncate text-[11px] ${value ? "" : "text-[var(--muted)]"}`}>
          {selected?.name || value || "Search pal eggs…"}
        </span>
        <Search size={11} className="ml-auto shrink-0 text-[var(--muted)]" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-[280px] max-w-[calc(100vw-32px)] rounded-md border border-white/10 bg-[#11131a] shadow-xl p-2">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${eggs.length} pal eggs…`}
            className="input input-sm w-full text-[11px] mb-1.5"
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <ul className="max-h-60 overflow-y-auto space-y-0.5">
            {results.map((e) => (
              <li key={e.id}>
                <button
                  type="button"
                  onClick={() => { onChange(e.id); setOpen(false); }}
                  className="w-full text-left flex items-center gap-2 px-1.5 py-1 rounded hover:bg-white/5"
                  data-testid={testId ? `${testId}-option-${e.id}` : undefined}
                >
                  {e.image
                    ? <img src={e.image} alt="" className="w-5 h-5 object-contain shrink-0" loading="lazy" />
                    : <span className="w-5 h-5 shrink-0 text-center text-[11px]">🥚</span>}
                  <span className="min-w-0 flex-1">
                    <span className="block text-[11px] text-[var(--bone)] truncate">{e.name}</span>
                    <span className="block text-[9px] text-[var(--muted)] truncate">
                      hatches 1 of {e.pals} pals · Lv {e.level[0]}-{e.level[1]}
                    </span>
                  </span>
                  {e.id === value && <Check size={11} className="text-[var(--teal)] shrink-0" />}
                </button>
              </li>
            ))}
            {results.length === 0 && (
              <li className="text-[11px] text-[var(--muted)] px-1.5 py-2">No match.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export function ItemPicker({ value, onChange, placeholder = "Search items & eggs…", testId, only }) {
  const allRows = useItemCatalog();
  const eggOptions = useEggOptions();
  // The Give catalog tags pal eggs as `item`, so `kind` alone can't be
  // trusted — gate on the authoritative hatch-pool id list (same source
  // the backend's `is_egg()` uses) so a dud egg can't be picked here.
  const eggIds = useMemo(() => new Set(eggOptions.map((e) => e.id)), [eggOptions]);
  const catalog = useMemo(() => {
    const isEgg = (c) => c.kind === "egg" || eggIds.has(c.id);
    return only === "egg" ? allRows.filter(isEgg) : allRows.filter((c) => !isEgg(c));
  }, [allRows, only, eggIds]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const selected = useMemo(
    () => catalog.find((c) => c.id === value) || null,
    [catalog, value],
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return catalog.slice(0, 40);
    return catalog
      .filter((c) => c.name.toLowerCase().includes(q) || c.id.toLowerCase().includes(q))
      .slice(0, 60);
  }, [catalog, query]);

  return (
    <div className="relative" ref={boxRef} data-testid={testId}>
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); setQuery(""); }}
        className="input input-sm w-full flex items-center gap-1.5 text-left"
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        {selected?.image && (
          <img src={selected.image} alt="" className="w-4 h-4 object-contain shrink-0" loading="lazy" />
        )}
        <span className={`truncate text-[11px] ${value ? "" : "text-[var(--muted)]"}`}>
          {selected?.name || value || placeholder}
        </span>
        <Search size={11} className="ml-auto shrink-0 text-[var(--muted)]" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-[280px] max-w-[calc(100vw-32px)] rounded-md border border-white/10 bg-[#11131a] shadow-xl p-2">
          <div className="flex items-center gap-1.5 mb-1.5">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${catalog.length} ${only === "egg" ? "pal eggs" : "items"}…`}
              className="input input-sm flex-1 text-[11px]"
              data-testid={testId ? `${testId}-search` : undefined}
            />
            {value && (
              <button
                type="button"
                onClick={() => { onChange(""); setOpen(false); }}
                className="px-1 text-[var(--muted)] hover:text-[var(--error)]"
                title="Clear"
                data-testid={testId ? `${testId}-clear` : undefined}
              >
                <X size={12} />
              </button>
            )}
          </div>
          <ul className="max-h-60 overflow-y-auto space-y-0.5">
            {results.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => { onChange(c.id); setOpen(false); }}
                  className="w-full text-left flex items-center gap-2 px-1.5 py-1 rounded hover:bg-white/5"
                  data-testid={testId ? `${testId}-option-${c.id}` : undefined}
                >
                  {c.image
                    ? <img src={c.image} alt="" className="w-5 h-5 object-contain shrink-0" loading="lazy" />
                    : <span className="w-5 h-5 shrink-0" />}
                  <span className="min-w-0 flex-1">
                    <span className="block text-[11px] text-[var(--bone)] truncate">{c.name}</span>
                    <span className="block text-[9px] font-mono text-[var(--muted)] truncate">{c.id}</span>
                  </span>
                  {c.kind === "egg" && (
                    <span className="text-[9px] uppercase text-amber-300/80 shrink-0">egg</span>
                  )}
                  {c.id === value && <Check size={11} className="text-[var(--teal)] shrink-0" />}
                </button>
              </li>
            ))}
            {results.length === 0 && (
              <li className="text-[11px] text-[var(--muted)] px-1.5 py-2">
                No match. You can still type a raw ItemID below.
              </li>
            )}
          </ul>
          <input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="…or paste a raw ItemID"
            className="input input-sm w-full mt-1.5 font-mono text-[10px]"
            data-testid={testId ? `${testId}-raw` : undefined}
          />
        </div>
      )}
    </div>
  );
}
